import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomContainer } from "../../../components/Containers";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import useDebounce from "../../../hooks/useDebounce";
import {
  setOrderCurrentPage,
  setOrderPageLimit,
  setOrderTotalCount,
  setOrderTotalPages,
} from "../../../lib/slices/paginationSlice";
import { getAllSupplierOrders } from "../../../services/supplier/orderApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "orderId",
  "name",
  "subTotal",
  "vat",
  "orderVatAmount",
  "totalBill",
  "actions",
];

const tableRows = [
  "created_date",
  "order_id",
  "order_user_name",
  "order_sub_total",
  "order_vat",
  "order_vat_amount",
  "order_final_total",
  "order_detail",
];

function SupplierAllSale() {
  // Translation Hook
  const { t } = useTranslation();
  // Dispatch Hook
  const dispatch = useDispatch();
  // Navigate Hook
  const navigate = useNavigate();
  // Loading State
  const [isLoading, setIsLoading] = useState();
  // All Orders State
  const [allOrders, setAllOrders] = useState([]);
  // User Data
  const userData = useSelector((state) => state.userData.user);
  // Get Pagination State
  const pagination = useSelector((state) => state.paginationReducer);
  // Order Data
  const order = useSelector(
    (state) => state.productSearchPageReducer.productSearchPage,
  );
  const debouncedSearchTerm = useDebounce(order.orderId, 1000);

  const fetchAllOrders = async (search, page, limit) => {
    const query = {
      orderId: search || "",
      page: page || 1,
      limit: limit || 20,
    };
    try {
      setIsLoading(true);
      const response = await getAllSupplierOrders(userData._id, query);
      if (response?.data.status === "success") {
        let orders = response?.data?.data.data;
        let pagination = response?.data?.data.pagination;
        setAllOrders(orders);
        dispatch(setOrderPageLimit(pagination.limit));
        dispatch(setOrderCurrentPage(pagination.page));
        dispatch(setOrderTotalCount(pagination.total));
        dispatch(setOrderTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchAllOrders(
        debouncedSearchTerm,
        pagination.orderPage,
        pagination.orderLimit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    pagination.orderPage,
    pagination.orderLimit,
  ]);

  const handleViewDetail = (row) => {
    navigate(`/supplier/sale/detail/${row?._id}`);
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setOrderCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setOrderPageLimit(limit));
    dispatch(setOrderCurrentPage(1));
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allOrders}
        showDynamicPagination={true}
        paginatedData={{
          page: pagination.orderPage,
          limit: pagination.orderLimit,
          total: pagination.orderTotal,
          totalPages: pagination.orderTotalPages,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        handleViewDetail={handleViewDetail}
        isLoading={isLoading}
      />
    );
  }, [allOrders, isLoading]);

  return (
    <CustomContainer
      primaryHeading={t("saleList")}
      secondaryHeading={t("saleListDescription")}
    >
      {renderTable}
    </CustomContainer>
  );
}

export default SupplierAllSale;
